import { RoomType, PublicEWebinarFragment } from '$/GraphQL/schema/schema';
import { minRangeAppearEnd } from '../Timeline/Marker/variables';

export class WebinarRoom {
  public endsAt: number;

  public constructor(
    public title: string,
    public type: RoomType,
    public appearAt: number,
    public duration: number,
    public anchor: 'start' | 'middle' | 'end',
    public caps: {
      hasInteractions: boolean;
      hasVideoPlayer: boolean;
      hideVideoPlayerStage: boolean;
      playsVideo: boolean;
      showAgenda: boolean;
      inSession: boolean;
    }
  ) {
    this.endsAt = appearAt + duration;
  }
  public get hasChat(): boolean {
    return this.caps.hasInteractions;
  }

  public get anchorAt(): number {
    switch (this.anchor) {
      case 'start':
        return this.appearAt + 10;
      case 'end':
        return this.appearAt + this.duration - 10;
      case 'middle':
        return (this.appearAt + this.duration) / 2;
    }
  }
}

export const DefaultWebinarRoom = {
  title: 'Presentation room',
  type: RoomType.Presentation,
  duration: 45 * 60,
  appearAt: 0,
  anchor: 'start',
};

const Room = (type: RoomType): number => {
  switch (type) {
    case RoomType.PreWaiting:
      return 0;
    case RoomType.Waiting:
      return 1;
    case RoomType.Presentation:
      return 2;
    case RoomType.Exit:
      return 3;
    default:
      return 4;
  }
};

export class Rooms extends Array<WebinarRoom> {
  constructor(...rooms: Array<WebinarRoom>) {
    super();
    this.push(...rooms);
  }

  static create(
    ewebinar?: Pick<
      PublicEWebinarFragment,
      'exitRoomDurationSecs' | 'duration' | 'waitingRoomDurationSecs'
    >,
    durationMilliseconds?: number
  ): Rooms {
    const waitingRoomDurationSecs = ewebinar?.waitingRoomDurationSecs || 15 * 60;

    // We Math.ceil one to assure we don't cut off the last second of audio
    const ewebinarDuration = Math.ceil(
      durationMilliseconds ?? (ewebinar?.duration ?? 45 * 60) + 0.999
    ); // Add 0.999 to ewebinar duration to take into account milliseconds
    const exitRoomDurationSecs = ewebinar?.exitRoomDurationSecs ?? 30 * 60;

    const rooms: WebinarRoom[] = [
      new WebinarRoom(
        'PreWaiting',
        RoomType.PreWaiting,
        -Number.MAX_SAFE_INTEGER,
        Number.MAX_SAFE_INTEGER - waitingRoomDurationSecs,
        'end',
        {
          hasInteractions: false,
          hasVideoPlayer: false,
          hideVideoPlayerStage: false,
          playsVideo: false,
          showAgenda: false,
          inSession: false,
        }
      ),
      new WebinarRoom(
        'Waiting room',
        RoomType.Waiting,
        0 - waitingRoomDurationSecs,
        waitingRoomDurationSecs,
        'end',
        {
          hasInteractions: true,
          hasVideoPlayer: true,
          hideVideoPlayerStage: false,
          playsVideo: false,
          showAgenda: false,
          inSession: true,
        }
      ),
      new WebinarRoom('Presentation room', RoomType.Presentation, 0, ewebinarDuration, 'middle', {
        hasInteractions: true,
        hasVideoPlayer: true,
        hideVideoPlayerStage: false,
        playsVideo: true,
        showAgenda: true,
        inSession: true,
      }),
      new WebinarRoom('Exit room', RoomType.Exit, ewebinarDuration, exitRoomDurationSecs, 'start', {
        hasInteractions: true,
        hasVideoPlayer: true,
        hideVideoPlayerStage: true,
        playsVideo: false,
        showAgenda: true,
        inSession: true,
      }),
      new WebinarRoom(
        'PostExit',
        RoomType.PostExit,
        ewebinarDuration + exitRoomDurationSecs,
        Number.MAX_SAFE_INTEGER,
        'start',
        {
          hasInteractions: false,
          hasVideoPlayer: false,
          hideVideoPlayerStage: false,
          playsVideo: false,
          showAgenda: false,
          inSession: false,
        }
      ),
    ];

    return new Rooms(...rooms);
  }

  // Calculate total duration
  public get domain(): [number, number] {
    return [0 - this.offset, this.maxDuration];
  }

  // Offset timeline numbers
  public get offset(): number {
    return this[Room(RoomType.Waiting)].duration;
  }

  // Calculate total duration
  public get maxDuration(): number {
    return this[Room(RoomType.Presentation)].duration + this[Room(RoomType.Exit)].duration;
  }

  public get maxReplayDuration(): number {
    return this[Room(RoomType.Presentation)].duration;
  }

  // Find EwebinarRoom based on `type` property
  public getRoom(type: RoomType): WebinarRoom {
    return this[Room(type)];
  }

  // Finds EwebinarRoom based on `playbackPosition`
  public getRoomAt(playbackPosition: number): WebinarRoom {
    for (let i = 0; i <= Room(RoomType.PostExit); i++) {
      if (
        playbackPosition >= this[i].appearAt &&
        Math.floor(playbackPosition) < this[i].appearAt + this[i].duration
      ) {
        return this[i];
      }
    }
    return this[Room(RoomType.PostExit)];
  }

  public playbackPositionFromDuo(appearAt: number, room: RoomType): number {
    return this.getRoom(room).appearAt + appearAt;
  }

  public getPlaybackPositionPresentation(playbackPosition: number): number {
    const room = this.getRoom(RoomType.Presentation);
    return playbackPosition > room.duration
      ? room.duration - 1 - minRangeAppearEnd
      : Math.max(room.appearAt, playbackPosition);
  }

  /**
   * Calc appearAt time for Interations.
   *
   * @param playbackPosition
   */
  public appearAtDuoFromPosition(playbackPosition: number): [number, RoomType] {
    let room = this.getRoomAt(playbackPosition);

    // Interactions never appear in PreWaiting or PostExit room.
    if (room.type === RoomType.PreWaiting) {
      room = this.getRoom(RoomType.Waiting);
      playbackPosition = Math.max(playbackPosition, this.getRoom(RoomType.Waiting).appearAt);
    }
    if (room.type === RoomType.PostExit) {
      room = this.getRoom(RoomType.Exit);
      playbackPosition = Math.min(playbackPosition, this.maxDuration - 1);
    }

    const appearAt = playbackPosition - room.appearAt;
    return [appearAt, room.type];
  }
}
